import type { InferContractRouterOutputs } from '@orpc/contract'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { baseContract } from 'pixai-contract/api/orpc'
import { contractClient, orpc } from './contract'

export function useCancelRunningTask() {
  return useMutation({
    ...orpc.task.cancelRunningTask.mutationOptions(),
  })
}

/**
 * Fetches an estimated wait time for a task of the given priority targeting
 * the given model. Pass `null` (or omit modelVersionId/generationModelId) to
 * disable the query. The server returns 404 when a prediction isn't available
 * (no queue-depth snapshot yet, upstream down, unknown model, etc.); we treat
 * that as "no hint to show" and don't retry.
 */
export function useTaskWaitTime(
  params:
    | {
        priority: number
        modelVersionId?: string
        generationModelId?: string
      }
    | null
    | undefined,
) {
  const enabled = Boolean(
    params && (params.modelVersionId || params.generationModelId),
  )
  return useQuery({
    ...orpc.task.predictWaitTime.queryOptions({
      input: {
        priority: params?.priority ?? 0,
        modelVersionId: params?.modelVersionId,
        generationModelId: params?.generationModelId,
      },
    }),
    enabled,
    // Server refreshes the queue-depth hash every 60s; re-poll on the same
    // cadence while the hint is visible so it doesn't go stale mid-session.
    // staleTime dedupes re-mounts within the refresh window; skip background
    // polling so inactive tabs don't keep hitting the ML upstream.
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  })
}

type AcquirePreGenerationResult = InferContractRouterOutputs<
  typeof baseContract
>['task']['acquirePreGenerated']

export async function acquirePreGeneratedTask(input: {
  sceneId: string
  tackId?: string
}): Promise<AcquirePreGenerationResult> {
  return contractClient.task.acquirePreGenerated(input)
}
